import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip /auth/me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (mounted) setUser(data);
      } catch (err) {
        // 401 here just means "not logged in" — expected for public pages.
        // Log non-auth errors so they aren't silently swallowed.
        if (err?.response?.status && err.response.status !== 401) {
          console.error("Failed to fetch current user:", err);
        }
        if (mounted) setUser(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("skintrack_token", data.access_token);
    setUser(data);
    return data;
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    if (data.access_token) localStorage.setItem("skintrack_token", data.access_token);
    setUser(data);
    return data;
  };
  const setUserFromGoogle = (data) => {
    setUser(data);
  };
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Intentional: clear local state even if server-side logout fails.
      console.warn("Server-side logout failed (clearing local state anyway):", err);
    }
    localStorage.removeItem("skintrack_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUserFromGoogle }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
